import { useEffect, useState } from "react";
import { Banknote, Building2, CircleCheck, CirclePause, Copy, FileText, KeyRound, LayoutDashboard, Mail, Megaphone, Package, Paperclip, Pencil, Plus, ReceiptText, Search, Settings2, ShieldCheck, Trash2, Users, Wallet, X } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { API, api, money } from "@/lib/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" }) : "—");

const NAV = [
  { group: "BERANDA", items: [{ key: "dashboard", label: "Dashboard", icon: LayoutDashboard }] },
  { group: "SAAS MANAGEMENT", items: [
    { key: "packages", label: "Paket Langganan", icon: Package },
    { key: "merchants", label: "Merchant & User", icon: Users },
    { key: "transactions", label: "Transaksi", icon: ReceiptText },
  ] },
  { group: "PENGATURAN", items: [{ key: "settings", label: "Pengaturan Aplikasi", icon: Settings2 }] },
];

function PackageModal({ initial, onClose, onSaved, notify }) {
  const [form, setForm] = useState(initial ? { ...initial, features: (initial.features || []).join("\n") } : { name: "", label: "", price_monthly: 0, max_users: 0, max_invoices: 0, features: "", is_active: true });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });
  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form, features: form.features.split("\n").map((f) => f.trim()).filter(Boolean) };
      const r = initial ? await api(`/admin/packages/${initial.id}`, { method: "PATCH", body: JSON.stringify(payload) }) : await api("/admin/packages", { method: "POST", body: JSON.stringify(payload) });
      onSaved(r);
    } catch (e) { notify(e.message, "error"); }
    setSaving(false);
  };
  return (
    <div className="modal-overlay" data-testid="package-modal" onClick={onClose}>
      <div className="modal-panel settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sm-head">
          <div><p className="eyebrow admin">PAKET LANGGANAN</p><h2>{initial ? `Ubah ${initial.name}` : "Tambah paket baru"}</h2></div>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="sm-body">
          <div className="tax-form-grid">
            <label>Kode paket<input data-testid="pkg-name-input" value={form.name} onChange={set("name")} placeholder="mis. STARTER" /></label>
            <label>Nama tampilan<input data-testid="pkg-label-input" value={form.label} onChange={set("label")} placeholder="mis. Starter" /></label>
            <label>Harga / bulan (Rp)<input data-testid="pkg-price-input" type="number" min="0" value={form.price_monthly} onChange={set("price_monthly")} /></label>
            <label>Maks pengguna (0 = tanpa batas)<input data-testid="pkg-users-input" type="number" min="0" value={form.max_users} onChange={set("max_users")} /></label>
            <label>Maks invoice / bulan (0 = tanpa batas)<input data-testid="pkg-invoices-input" type="number" min="0" value={form.max_invoices} onChange={set("max_invoices")} /></label>
            <label>Status
              <select data-testid="pkg-active-select" value={form.is_active ? "1" : "0"} onChange={(e) => setForm({ ...form, is_active: e.target.value === "1" })}>
                <option value="1">Aktif (bisa dipilih)</option><option value="0">Nonaktif</option>
              </select>
            </label>
          </div>
          <p className="sm-field-label">Daftar fitur <span className="muted-inline">— satu fitur per baris</span></p>
          <textarea data-testid="pkg-features-input" className="pkg-features" rows={4} value={form.features} onChange={set("features")} placeholder={"Semua modul dasar\nMaks 10 pengguna"} />
        </div>
        <div className="sm-foot">
          <button className="secondary-btn" onClick={onClose}>Batal</button>
          <button data-testid="pkg-save-button" className="primary-btn" disabled={!form.name.trim() || !form.label.trim() || saving} onClick={submit}>{saving ? "Menyimpan..." : "Simpan Paket"}</button>
        </div>
      </div>
    </div>
  );
}

function TransactionModal({ tenants, packages, onClose, onSaved, notify }) {
  const [form, setForm] = useState({ tenant_id: "", package_name: "", months: 1, amount: 0, note: "" });
  const [saving, setSaving] = useState(false);
  const pick = (k, v) => {
    const next = { ...form, [k]: v };
    const pkg = packages.find((p) => p.name === next.package_name);
    if (pkg && (k === "package_name" || k === "months")) next.amount = pkg.price_monthly * Number(next.months || 1);
    setForm(next);
  };
  const submit = async () => {
    setSaving(true);
    try { onSaved(await api("/admin/transactions", { method: "POST", body: JSON.stringify({ ...form, months: Number(form.months), amount: Number(form.amount) }) })); }
    catch (e) { notify(e.message, "error"); }
    setSaving(false);
  };
  return (
    <div className="modal-overlay" data-testid="transaction-modal" onClick={onClose}>
      <div className="modal-panel settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sm-head">
          <div><p className="eyebrow admin">TRANSAKSI LANGGANAN</p><h2>Catat pembayaran langganan</h2><p className="muted sm-sub">Paket & masa aktif tenant akan otomatis diperbarui setelah transaksi disimpan.</p></div>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="sm-body">
          <div className="tax-form-grid">
            <label>Perusahaan
              <select data-testid="tx-tenant-select" value={form.tenant_id} onChange={(e) => pick("tenant_id", e.target.value)}>
                <option value="" disabled>Pilih perusahaan</option>
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <label>Paket
              <select data-testid="tx-package-select" value={form.package_name} onChange={(e) => pick("package_name", e.target.value)}>
                <option value="" disabled>Pilih paket</option>
                {packages.filter((p) => p.is_active).map((p) => <option key={p.id} value={p.name}>{`${p.label} — ${money(p.price_monthly)}/bln`}</option>)}
              </select>
            </label>
            <label>Durasi (bulan)<input data-testid="tx-months-input" type="number" min="1" max="36" value={form.months} onChange={(e) => pick("months", e.target.value)} /></label>
            <label>Jumlah dibayar (Rp)<input data-testid="tx-amount-input" type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></label>
            <label className="span-2">Catatan<input data-testid="tx-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="mis. transfer BCA 13 Agu" /></label>
          </div>
        </div>
        <div className="sm-foot">
          <button className="secondary-btn" onClick={onClose}>Batal</button>
          <button data-testid="tx-save-button" className="primary-btn" disabled={!form.tenant_id || !form.package_name || saving} onClick={submit}>{saving ? "Menyimpan..." : "Simpan Transaksi"}</button>
        </div>
      </div>
    </div>
  );
}

function BankSettings({ initial, onSaved, notify }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const save = async () => {
    setSaving(true);
    try { onSaved(await api("/admin/settings", { method: "PATCH", body: JSON.stringify({ bank_info: form }) })); notify("Rekening bank pembayaran disimpan"); }
    catch (e) { notify(e.message, "error"); }
    setSaving(false);
  };
  return (
    <div className="surface bank-settings" data-testid="bank-settings-card">
      <div className="section-head">
        <div><h3><Banknote size={16} /> Rekening Bank Pembayaran</h3><p className="muted">Rekening tujuan transfer yang tampil di halaman Paket & Langganan seluruh tenant.</p></div>
      </div>
      <div className="bank-preview" data-testid="bank-settings-preview">
        <small>{form.bank || "—"} — a.n. {form.account_name || "—"}</small>
        <strong>{form.account_number || "—"}</strong>
      </div>
      <div className="tax-form-grid">
        <label>Nama bank<input data-testid="bank-name-input" value={form.bank} onChange={set("bank")} placeholder="mis. BCA" /></label>
        <label>Nomor rekening<input data-testid="bank-number-input" value={form.account_number} onChange={set("account_number")} placeholder="mis. 8830-112-233" /></label>
        <label className="span-2">Atas nama<input data-testid="bank-holder-input" value={form.account_name} onChange={set("account_name")} placeholder="mis. PT AIC Accounting Indonesia" /></label>
        <label className="span-2">Instruksi pembayaran<input data-testid="bank-instruction-input" value={form.instruction} onChange={set("instruction")} placeholder="Instruksi yang tampil untuk tenant" /></label>
      </div>
      <div className="bank-settings-foot">
        <button data-testid="bank-save-button" className="primary-btn nowrap" disabled={!dirty || saving || !form.bank.trim() || !form.account_number.trim() || !form.account_name.trim()} onClick={save}>{saving ? "Menyimpan..." : "Simpan Rekening"}</button>
      </div>
    </div>
  );
}

function ResetPassModal({ target, onClose, onTemp, notify }) {
  const [modeCustom, setModeCustom] = useState(false);
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const weak = pw.length < 8 || !/[A-Za-z]/.test(pw) || !/\d/.test(pw);
  const submit = async () => {
    setSaving(true);
    try {
      const r = await api(`/admin/users/${target.id}/reset-password`, { method: "POST", body: JSON.stringify(modeCustom ? { password: pw } : {}) });
      if (r.custom) notify(`Kata sandi ${r.email} berhasil diganti`);
      else onTemp(r);
      onClose();
    } catch (e) { notify(e.message, "error"); }
    setSaving(false);
  };
  return (
    <div className="modal-overlay" data-testid="reset-pass-modal">
      <div className="modal-panel settings-modal">
        <div className="sm-head">
          <div><p className="eyebrow admin">KEAMANAN AKUN</p><h2>Reset Kata Sandi</h2>
            <p className="muted sm-sub">{target.name} · {target.email}</p></div>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="sm-body">
          <div className="reset-choice">
            <button data-testid="reset-mode-auto" className={`reset-opt ${!modeCustom ? "on" : ""}`} onClick={() => setModeCustom(false)}>
              <b>Buat sandi sementara</b><small>Sandi acak dibuat otomatis — pengguna wajib menggantinya saat login berikutnya.</small>
            </button>
            <button data-testid="reset-mode-custom" className={`reset-opt ${modeCustom ? "on" : ""}`} onClick={() => setModeCustom(true)}>
              <b>Tentukan sandi baru</b><small>Anda mengetik sandi baru untuk pengguna ini (min. 8 karakter, huruf & angka).</small>
            </button>
          </div>
          {modeCustom && (
            <label className="cp-full">Kata sandi baru
              <input data-testid="reset-custom-input" type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="mis. SandiBaru2026" autoFocus />
              {pw && weak && <small className="field-hint" style={{ color: "#b3402c" }}>Minimal 8 karakter dengan kombinasi huruf dan angka.</small>}
            </label>
          )}
          <p className="field-hint">Sesi login, perangkat terpercaya, dan kode OTP pengguna ini akan dicabut demi keamanan.</p>
        </div>
        <div className="sm-foot">
          <button className="secondary-btn" onClick={onClose}>Batal</button>
          <button data-testid="reset-pass-submit" className="primary-btn" disabled={saving || (modeCustom && weak)} onClick={submit}>{saving ? "Memproses..." : modeCustom ? "Ganti Kata Sandi" : "Buat Sandi Sementara"}</button>
        </div>
      </div>
    </div>
  );
}

function LandingSettings({ initial, onSaved, notify }) {
  const [form, setForm] = useState({ ...initial, featuresText: (initial.features || []).map((f) => `${f.title} | ${f.desc}`).join("\n") });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const { featuresText, ...rest } = form;
      const features = featuresText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
        const [title, ...d] = l.split("|");
        return { title: title.trim(), desc: d.join("|").trim() };
      });
      onSaved(await api("/admin/settings", { method: "PATCH", body: JSON.stringify({ landing: { ...rest, features } }) }));
      notify("Konten landing page disimpan");
    } catch (e) { notify(e.message, "error"); }
    setSaving(false);
  };
  return (
    <div className="surface bank-settings" data-testid="landing-settings-card">
      <div className="section-head">
        <div><h3><Megaphone size={16} /> Landing Page & Marketing</h3><p className="muted">Konten halaman depan publik — nama aplikasi, teks hero, fitur, harga (otomatis dari Paket), WhatsApp, dan kontak.</p></div>
      </div>
      <div className="tax-form-grid">
        <label>Nama aplikasi<input data-testid="landing-appname-input" value={form.app_name} onChange={set("app_name")} /></label>
        <label>Tagline<input data-testid="landing-tagline-input" value={form.tagline} onChange={set("tagline")} /></label>
        <label className="span-2">Judul hero<input data-testid="landing-hero-title-input" value={form.hero_title} onChange={set("hero_title")} /></label>
        <label className="span-2">Subjudul hero<input data-testid="landing-hero-subtitle-input" value={form.hero_subtitle} onChange={set("hero_subtitle")} /></label>
      </div>
      <label className="tpl-field">Fitur unggulan <span className="muted-inline">— satu baris per fitur, format: Judul | Deskripsi</span>
        <textarea data-testid="landing-features-input" rows={6} value={form.featuresText} onChange={set("featuresText")} />
      </label>
      <div className="tax-form-grid">
        <label>Nomor WhatsApp <span className="muted-inline">(format 62…)</span><input data-testid="landing-wa-input" value={form.whatsapp_number} onChange={set("whatsapp_number")} placeholder="mis. 6281234567890" /></label>
        <label>Pesan WhatsApp otomatis<input data-testid="landing-wa-msg-input" value={form.whatsapp_message} onChange={set("whatsapp_message")} /></label>
        <label>Email kontak<input data-testid="landing-email-input" value={form.contact_email} onChange={set("contact_email")} /></label>
        <label>Telepon kontak<input data-testid="landing-phone-input" value={form.contact_phone} onChange={set("contact_phone")} /></label>
        <label className="span-2">Alamat<input data-testid="landing-address-input" value={form.contact_address} onChange={set("contact_address")} /></label>
        <label className="span-2">Teks footer<input data-testid="landing-footer-input" value={form.footer_text} onChange={set("footer_text")} /></label>
      </div>
      <div className="bank-settings-foot">
        <a className="secondary-btn nowrap" href="/" target="_blank" rel="noreferrer">Lihat Landing Page</a>
        <button data-testid="landing-save-button" className="primary-btn nowrap" disabled={saving || !form.app_name.trim()} onClick={save}>{saving ? "Menyimpan..." : "Simpan Landing Page"}</button>
      </div>
    </div>
  );
}

function SmtpSettings({ initial, onSaved, notify }) {
  const [form, setForm] = useState({ host: initial.host, port: initial.port, username: initial.username, from_email: initial.from_email, from_name: initial.from_name, password: "" });
  const [testTo, setTestTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try { onSaved(await api("/admin/settings", { method: "PATCH", body: JSON.stringify({ smtp: form }) })); notify("Pengaturan SMTP disimpan"); }
    catch (e) { notify(e.message, "error"); }
    setSaving(false);
  };
  const test = async () => {
    setTesting(true);
    try { const r = await api("/admin/settings/smtp-test", { method: "POST", body: JSON.stringify({ to: testTo }) }); notify(r.message); }
    catch (e) { notify(e.message, "error"); }
    setTesting(false);
  };
  return (
    <div className="surface bank-settings" data-testid="smtp-settings-card">
      <div className="section-head">
        <div><h3><Mail size={16} /> Server Email (SMTP)</h3><p className="muted">Untuk verifikasi email pendaftar, reset kata sandi, dan notifikasi pembayaran. Kosong = fitur email nonaktif (pendaftar langsung aktif tanpa verifikasi).</p></div>
        <span className={`status ${initial.password_set && initial.host ? "paid" : "void"}`}>{initial.password_set && initial.host ? "Terpasang" : "Belum diatur"}</span>
      </div>
      <div className="tax-form-grid">
        <label>Host SMTP<input data-testid="smtp-host-input" value={form.host} onChange={set("host")} placeholder="mis. smtp.gmail.com / smtp-relay.brevo.com" /></label>
        <label>Port<input data-testid="smtp-port-input" type="number" value={form.port} onChange={set("port")} placeholder="587 (TLS) atau 465 (SSL)" /></label>
        <label>Username
          <input data-testid="smtp-username-input" value={form.username} onChange={set("username")} placeholder="alamat email lengkap" />
          <small className="field-hint">Untuk Gmail/kebanyakan provider: isi <b>alamat email lengkap</b> (mis. nama@gmail.com), bukan nama tampilan.</small>
        </label>
        <label>Password / API Key
          <input data-testid="smtp-password-input" type="password" value={form.password} onChange={set("password")} placeholder={initial.password_set ? "•••••••• (kosongkan jika tidak diubah)" : "App password / API key SMTP"} />
          <small className="field-hint">Gmail: gunakan <b>App Password</b> 16 karakter (myaccount.google.com → Keamanan → App passwords), bukan kata sandi akun.</small>
        </label>
        <label>Email pengirim<input data-testid="smtp-from-email-input" value={form.from_email} onChange={set("from_email")} placeholder="mis. noreply@perusahaan.co.id" /></label>
        <label>Nama pengirim<input data-testid="smtp-from-name-input" value={form.from_name} onChange={set("from_name")} placeholder="mis. AIC Accounting" /></label>
      </div>
      {initial.last_error && <div className="smtp-error" data-testid="smtp-last-error"><b>Pengiriman email terakhir gagal:</b> {initial.last_error}</div>}
      <p className="field-hint">Untuk menonaktifkan email: kosongkan Host, Username, dan Email pengirim lalu simpan — password tersimpan ikut terhapus.</p>
      <div className="smtp-foot">
        <div className="smtp-test">
          <input data-testid="smtp-test-to-input" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="email tujuan uji coba" />
          <button data-testid="smtp-test-button" className="secondary-btn nowrap" disabled={!testTo.includes("@") || testing} onClick={test}>{testing ? "Mengirim..." : "Kirim Email Uji"}</button>
        </div>
        <button data-testid="smtp-save-button" className="primary-btn nowrap" disabled={saving} onClick={save}>{saving ? "Menyimpan..." : "Simpan SMTP"}</button>
      </div>
    </div>
  );
}

function TemplatesEditor({ templates, meta, onSaved, notify }) {
  const keys = Object.keys(meta || {});
  const [active, setActive] = useState(keys[0]);
  const [drafts, setDrafts] = useState(templates);
  const [saving, setSaving] = useState(false);
  const cur = drafts[active] || { subject: "", body: "" };
  const setField = (f) => (e) => setDrafts({ ...drafts, [active]: { ...cur, [f]: e.target.value } });
  const save = async () => {
    setSaving(true);
    try { onSaved(await api("/admin/settings", { method: "PATCH", body: JSON.stringify({ email_templates: drafts }) })); notify("Template email disimpan"); }
    catch (e) { notify(e.message, "error"); }
    setSaving(false);
  };
  return (
    <div className="surface bank-settings" data-testid="templates-card">
      <div className="section-head"><div><h3><FileText size={16} /> Template Email</h3><p className="muted">Sesuaikan subjek & isi email yang dikirim ke pengguna. Gunakan placeholder di bawah — akan diganti otomatis.</p></div></div>
      <div className="users-tabs tpl-tabs">
        {keys.map((k) => <button key={k} data-testid={`tpl-tab-${k}`} className={`contact-tab ${active === k ? "active" : ""}`} onClick={() => setActive(k)}>{meta[k].label}</button>)}
      </div>
      <div className="tpl-placeholders">{(meta[active]?.placeholders || []).map((p) => <code key={p}>{p}</code>)}</div>
      <label className="tpl-field">Subjek<input data-testid="tpl-subject-input" value={cur.subject} onChange={setField("subject")} /></label>
      <label className="tpl-field">Isi email<textarea data-testid="tpl-body-input" rows={7} value={cur.body} onChange={setField("body")} /></label>
      <div className="bank-settings-foot">
        <button data-testid="tpl-save-button" className="primary-btn nowrap" disabled={saving} onClick={save}>{saving ? "Menyimpan..." : "Simpan Template"}</button>
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const [section, setSection] = useState("dashboard");
  const [data, setData] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [packages, setPackages] = useState([]);
  const [platformUsers, setPlatformUsers] = useState([]);
  const [txs, setTxs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [term, setTerm] = useState("");
  const [subTab, setSubTab] = useState("merchant");
  const [pkgModal, setPkgModal] = useState(null);
  const [txModal, setTxModal] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [proofView, setProofView] = useState(null);
  const [tempPass, setTempPass] = useState(null);
  const [resetPass, setResetPass] = useState(null);
  const [message, setMessage] = useState(null);
  const notify = (text, type = "success") => { setMessage({ text, type }); setTimeout(() => setMessage(null), 5000); };
  const load = () => {
    api("/admin/overview").then(setData).catch((e) => notify(e.message, "error"));
    api("/admin/tenants").then(setTenants).catch(() => {});
    api("/admin/packages").then(setPackages).catch(() => {});
    api("/admin/users").then(setPlatformUsers).catch(() => {});
    api("/admin/transactions").then(setTxs).catch(() => {});
    api("/admin/settings").then(setSettings).catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line
  const act = async (fn, ok) => { try { await fn(); if (ok) notify(ok); load(); } catch (e) { notify(e.message, "error"); } };
  const k = data?.kpis || {};
  const rows = tenants.filter((t) => !term || t.name?.toLowerCase().includes(term.toLowerCase()) || t.owner_email?.toLowerCase().includes(term.toLowerCase()));
  const userRows = platformUsers.filter((u) => !term || u.name?.toLowerCase().includes(term.toLowerCase()) || u.email?.toLowerCase().includes(term.toLowerCase()) || u.tenant_name?.toLowerCase().includes(term.toLowerCase()));
  return (
    <div className="admin-layout" data-testid="admin-panel">
      <aside className="admin-subnav">
        {NAV.map((g) => (
          <div key={g.group}>
            <p className="admin-nav-group">{g.group}</p>
            {g.items.map(({ key, label, icon: Icon }) => (
              <button key={key} data-testid={`admin-nav-${key}`} className={`admin-nav-item ${section === key ? "active" : ""}`} onClick={() => { setSection(key); setTerm(""); }}>
                <Icon size={15} /> {label}
              </button>
            ))}
          </div>
        ))}
      </aside>
      <div className="admin-content">
        {message && <div data-testid="admin-message" className={`success-message ${message.type === "error" ? "error" : ""}`}>{message.text}<button onClick={() => setMessage(null)}><X size={14} /></button></div>}
        {section === "dashboard" && (
          <>
            <div className="page-heading"><div><p className="eyebrow admin">ADMIN PLATFORM · KHUSUS DEVELOPER</p><h1>Dashboard Admin <ShieldCheck size={22} className="admin-shield" /></h1><p className="muted">Ringkasan seluruh tenant & pengguna di platform.</p></div></div>
            <div className="kpi-grid">
              {[
                { label: "Total Merchant", value: k.tenants, icon: Building2, tone: "teal", meta: `${k.new_tenants_month || 0} baru bulan ini` },
                { label: "Total Pengguna", value: k.users, icon: Users, tone: "blue", meta: `${k.active_users || 0} aktif` },
                { label: "Revenue Bulan Ini", value: money(k.revenue_month || 0), icon: Wallet, tone: "green", meta: `Total sepanjang waktu ${money(k.revenue_total || 0)}` },
                { label: "Total Invoice Tenant", value: k.invoices, icon: FileText, tone: "amber", meta: `${k.journals || 0} jurnal terposting` },
              ].map(({ label, value, icon: Icon, tone, meta }) => (
                <div className="kpi-card v2" data-testid={`admin-kpi-${label.toLowerCase().replaceAll(" ", "-")}`} key={label}>
                  <div className="kpi-top"><span className={`kpi-ic ${tone}`}><Icon size={16} /></span><span className="kpi-label">{label}</span></div>
                  <strong>{value ?? "—"}</strong>
                  <small className="kpi-meta">{meta}</small>
                </div>
              ))}
            </div>
            <div className="content-grid">
              <section className="surface chart-card">
                <div className="section-head"><div><h3>Pertumbuhan pendaftaran</h3><p className="muted">Perusahaan & pengguna baru per bulan</p></div>
                  <div className="legend v2"><span><i className="legend-dot revenue" />Perusahaan</span><span><i className="legend-dot expense" />Pengguna</span></div></div>
                <div data-testid="admin-signup-chart">
                  <ResponsiveContainer width="100%" height={230}>
                    <BarChart data={data?.signup_trend || []} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="4 6" vertical={false} stroke="#e8edf1" />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#8b99aa" }} tickLine={false} axisLine={{ stroke: "#e6ebf0" }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#8b99aa" }} tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e3e9ee", fontSize: 11 }} formatter={(v, n) => [v, n === "tenants" ? "Perusahaan baru" : "Pengguna baru"]} />
                      <Bar dataKey="tenants" fill="#0d9488" radius={[4, 4, 0, 0]} maxBarSize={22} />
                      <Bar dataKey="users" fill="#cdd8e1" radius={[4, 4, 0, 0]} maxBarSize={22} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>
              <section className="surface receivable-card">
                <div className="section-head"><div><h3>Pendaftar terbaru</h3><p className="muted">6 perusahaan terakhir</p></div></div>
                {(data?.recent || []).map((t) => (
                  <div className="admin-recent-row" key={t.id}>
                    <span className="role-avatar"><Building2 size={14} /></span>
                    <span className="arr-body"><b>{t.name}</b><small>{t.owner} · {fmtDate(t.created_at)}</small></span>
                    <span className={`status ${t.status === "ACTIVE" ? "paid" : "void"}`}>{t.status === "ACTIVE" ? "Aktif" : "Suspend"}</span>
                  </div>
                ))}
                <div className="admin-plans">{Object.entries(data?.plans || {}).map(([p, n]) => <span key={p} className="mod-chip">{p}: <b>{n}</b></span>)}</div>
              </section>
            </div>
          </>
        )}
        {section === "packages" && (
          <>
            <div className="page-heading"><div><p className="eyebrow admin">MASTER DATA SAAS</p><h1>Paket Langganan</h1><p className="muted">Kelola paket, harga, dan batasan. Batas pengguna & invoice ditegakkan otomatis ke setiap tenant.</p></div>
              <button data-testid="add-package-button" className="primary-btn nowrap" onClick={() => setPkgModal({})}><Plus size={14} /> Tambah Paket</button></div>
            <div className="pkg-grid" data-testid="packages-grid">
              {packages.map((p, i) => (
                <div key={p.id} className={`surface pkg-card ${!p.is_active ? "off" : ""}`} data-testid={`package-card-${i}`}>
                  <div className="pkg-head">
                    <div><span className="mod-chip all">{p.name}</span><h3>{p.label}</h3></div>
                    <div className="admin-actions">
                      <button data-testid={`pkg-edit-${i}`} className="secondary-btn small" onClick={() => setPkgModal(p)}><Pencil size={12} /></button>
                      <button data-testid={`pkg-delete-${i}`} className="danger-btn small" onClick={() => setConfirmDel({ kind: "package", item: p })}><Trash2 size={12} /></button>
                    </div>
                  </div>
                  <div className="pkg-price">{p.price_monthly > 0 ? <>{money(p.price_monthly)}<small>/bulan</small></> : "Gratis"}</div>
                  <div className="pkg-limits">
                    <span>{p.max_users > 0 ? `Maks ${p.max_users} pengguna` : "Pengguna tanpa batas"}</span>
                    <span>{p.max_invoices > 0 ? `Maks ${p.max_invoices} invoice/bln` : "Invoice tanpa batas"}</span>
                  </div>
                  <ul className="pkg-features-list">{(p.features || []).map((f) => <li key={f}><CircleCheck size={12} /> {f}</li>)}</ul>
                  <div className="pkg-foot">
                    <span className={`status ${p.is_active ? "paid" : "void"}`}>{p.is_active ? "Aktif" : "Nonaktif"}</span>
                    <small>{tenants.filter((t) => t.plan === p.name).length} tenant memakai</small>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {section === "merchants" && (
          <>
            <div className="page-heading"><div><p className="eyebrow admin">SAAS MANAGEMENT</p><h1>Merchant & User</h1><p className="muted">Kelola seluruh perusahaan dan akun pengguna di platform.</p></div></div>
            {tempPass && <div className="temp-pass-box" data-testid="admin-temp-pass"><ShieldCheck size={16} /><div><b>Kata sandi {tempPass.email} direset.</b><p>Sandi sementara: <code data-testid="admin-temp-pass-value">{tempPass.temp_password}</code> — pengguna wajib menggantinya saat login.</p></div><button className="secondary-btn small" onClick={() => navigator.clipboard?.writeText(tempPass.temp_password)}><Copy size={13} /> Salin</button><button className="icon-btn" onClick={() => setTempPass(null)}><X size={15} /></button></div>}
            <div className="users-toolbar">
              <div className="users-tabs">
                <button data-testid="admin-subtab-merchant" className={`contact-tab ${subTab === "merchant" ? "active" : ""}`} onClick={() => setSubTab("merchant")}>Merchant ({tenants.length})</button>
                <button data-testid="admin-subtab-users" className={`contact-tab ${subTab === "users" ? "active" : ""}`} onClick={() => setSubTab("users")}>Pengguna ({platformUsers.length})</button>
              </div>
              <div className="toolbar-spacer" />
              <div className="search-box"><Search size={14} /><input data-testid="admin-search" placeholder="Cari..." value={term} onChange={(e) => setTerm(e.target.value)} /></div>
            </div>
            {subTab === "merchant" ? (
              <div className="surface workflow-table-wrap auto-h">
                <table className="workflow-table" data-testid="admin-tenants-table">
                  <thead><tr><th>PERUSAHAAN</th><th>OWNER</th><th>PAKET</th><th>MASA AKTIF</th><th>PENGGUNA</th><th>INVOICE</th><th>STATUS</th><th className="right">TINDAKAN</th></tr></thead>
                  <tbody>
                    {rows.map((t, i) => (
                      <tr key={t.id} data-testid={`admin-tenant-row-${i}`}>
                        <td><strong>{t.name}</strong><small className="block-sub">{fmtDate(t.created_at)}</small></td>
                        <td><div className="user-cell"><span><strong>{t.owner_name}</strong><small>{t.owner_email}</small></span></div></td>
                        <td>
                          <select data-testid={`admin-plan-${i}`} className="admin-plan-select" value={t.plan} onChange={(e) => act(() => api(`/admin/tenants/${t.id}`, { method: "PATCH", body: JSON.stringify({ plan: e.target.value }) }), `Paket ${t.name} → ${e.target.value}`)}>
                            {packages.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                          </select>
                        </td>
                        <td>{t.plan_expires_at ? fmtDate(t.plan_expires_at) : "—"}</td>
                        <td>{t.users}</td>
                        <td>{t.invoices}</td>
                        <td><span className={`status ${t.status === "ACTIVE" ? "paid" : "void"}`}>{t.status === "ACTIVE" ? "Aktif" : "Suspend"}</span></td>
                        <td className="right">
                          <div className="admin-actions">
                            {t.owner_id && !t.owner_is_superadmin && (
                              <button data-testid={`admin-reset-owner-${i}`} className="secondary-btn small" title="Reset / ganti kata sandi Owner"
                                onClick={() => setResetPass({ id: t.owner_id, name: t.owner_name, email: t.owner_email })}><KeyRound size={13} /></button>
                            )}
                            <button data-testid={`admin-toggle-${i}`} className="secondary-btn small" title={t.status === "ACTIVE" ? "Tangguhkan" : "Aktifkan"}
                              onClick={() => act(() => api(`/admin/tenants/${t.id}`, { method: "PATCH", body: JSON.stringify({ status: t.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE" }) }), t.status === "ACTIVE" ? `${t.name} ditangguhkan` : `${t.name} diaktifkan`)}>
                              {t.status === "ACTIVE" ? <CirclePause size={13} /> : <CircleCheck size={13} />}
                            </button>
                            <button data-testid={`admin-delete-${i}`} className="danger-btn small" title="Hapus permanen" onClick={() => setConfirmDel({ kind: "tenant", item: t })}><Trash2 size={13} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="surface workflow-table-wrap auto-h">
                <table className="workflow-table" data-testid="admin-users-table">
                  <thead><tr><th>PENGGUNA</th><th>PERUSAHAAN</th><th>PERAN</th><th>STATUS</th><th className="right">TINDAKAN</th></tr></thead>
                  <tbody>
                    {userRows.map((u, i) => (
                      <tr key={u.id} data-testid={`admin-user-row-${i}`}>
                        <td><div className="user-cell"><span className="user-avatar">{(u.name || "?").slice(0, 2).toUpperCase()}</span><span><strong>{u.name}</strong><small>{u.email}</small></span></div></td>
                        <td>{u.tenant_name}</td>
                        <td>{u.is_superadmin ? <span className="role-chip">Developer</span> : u.role === "Owner" ? "Owner" : (u.roles || []).join(", ") || "Member"}</td>
                        <td><span className={`status ${u.is_active !== false ? "paid" : "void"}`}>{u.is_active !== false ? "Aktif" : "Nonaktif"}</span></td>
                        <td className="right">
                          {!u.is_superadmin && (
                            <div className="admin-actions">
                              <button data-testid={`admin-user-reset-${i}`} className="secondary-btn small nowrap" onClick={() => setResetPass({ id: u.id, name: u.name, email: u.email })}><KeyRound size={12} /> Reset sandi</button>
                              <button data-testid={`admin-user-toggle-${i}`} className="secondary-btn small nowrap" onClick={() => act(() => api(`/admin/users/${u.id}`, { method: "PATCH", body: JSON.stringify({ is_active: u.is_active === false }) }), u.is_active !== false ? `${u.name} dinonaktifkan` : `${u.name} diaktifkan`)}>{u.is_active !== false ? "Nonaktifkan" : "Aktifkan"}</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
        {section === "transactions" && (
          <>
            <div className="page-heading"><div><p className="eyebrow admin">SAAS MANAGEMENT</p><h1>Transaksi Langganan</h1><p className="muted">Catat pembayaran paket — masa aktif tenant diperbarui otomatis.</p></div>
              <button data-testid="add-transaction-button" className="primary-btn nowrap" onClick={() => setTxModal(true)}><Plus size={14} /> Catat Transaksi</button></div>
            <div className="surface workflow-table-wrap auto-h">
              <table className="workflow-table" data-testid="admin-transactions-table">
                <thead><tr><th>NO. TRANSAKSI</th><th>PERUSAHAAN</th><th>PAKET</th><th>DURASI</th><th>TANGGAL</th><th>AKTIF S/D</th><th>STATUS</th><th>BUKTI</th><th className="right">JUMLAH</th><th className="right">TINDAKAN</th></tr></thead>
                <tbody>
                  {txs.length === 0 && <tr><td colSpan="10" className="empty-cell" data-testid="tx-empty">Belum ada transaksi langganan — klik "Catat Transaksi".</td></tr>}
                  {txs.map((t, i) => (
                    <tr key={t.id} data-testid={`admin-tx-row-${i}`}>
                      <td><strong>{t.number}</strong>{t.note && <small className="block-sub">{t.note}</small>}{t.requested_by && <small className="block-sub">diminta {t.requested_by}</small>}</td>
                      <td>{t.tenant_name}</td>
                      <td><span className="mod-chip all">{t.package_name}</span></td>
                      <td>{t.months} bulan</td>
                      <td>{fmtDate(t.date)}</td>
                      <td>{fmtDate(t.expires_at)}</td>
                      <td><span className={`status ${t.status === "PAID" ? "paid" : t.status === "PENDING" ? "partial" : "void"}`}>{t.status === "PAID" ? "Lunas" : t.status === "PENDING" ? "Menunggu" : "Ditolak"}</span></td>
                      <td>{t.proof_mime ? <button data-testid={`admin-tx-proof-${i}`} className="secondary-btn small nowrap" onClick={() => setProofView(t)}><Paperclip size={12} /> Lihat</button> : <small className="muted-inline">—</small>}</td>
                      <td className="right mono">{money(t.amount)}{t.unique_code ? <small className="block-sub uc-chip">kode unik {t.unique_code}</small> : null}</td>
                      <td className="right">
                        <div className="admin-actions">
                          {t.status === "PENDING" && <>
                            <button data-testid={`admin-tx-confirm-${i}`} className="primary-btn small nowrap" onClick={() => act(() => api(`/admin/transactions/${t.id}`, { method: "PATCH", body: JSON.stringify({ action: "confirm" }) }), `Pembayaran ${t.tenant_name} dikonfirmasi`)}>Konfirmasi</button>
                            <button data-testid={`admin-tx-reject-${i}`} className="secondary-btn small nowrap" onClick={() => act(() => api(`/admin/transactions/${t.id}`, { method: "PATCH", body: JSON.stringify({ action: "reject" }) }), "Permintaan ditolak")}>Tolak</button>
                          </>}
                          <button data-testid={`admin-tx-delete-${i}`} className="danger-btn small" onClick={() => setConfirmDel({ kind: "tx", item: t })}><Trash2 size={12} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {section === "settings" && settings && (
          <>
            <div className="page-heading"><div><p className="eyebrow admin">PENGATURAN</p><h1>Pengaturan Aplikasi</h1><p className="muted">Kontrol perilaku platform untuk seluruh pendaftar.</p></div></div>
            <div className="surface app-settings">
              <div className="setting-row">
                <div><b>Buka pendaftaran publik</b><small>Jika dimatikan, halaman daftar menolak pendaftar baru.</small></div>
                <button data-testid="setting-registration-toggle" className={`toggle ${settings.allow_registration ? "on" : ""}`} onClick={() => act(async () => setSettings(await api("/admin/settings", { method: "PATCH", body: JSON.stringify({ allow_registration: !settings.allow_registration }) })), "Pengaturan disimpan")}><i /></button>
              </div>
              <div className="setting-row">
                <div><b>OTP perangkat baru (2FA email)</b><small>Minta kode 6 digit via email saat pengguna masuk dari perangkat baru atau setelah keluar. Butuh SMTP aktif.</small></div>
                <button data-testid="setting-otp-toggle" className={`toggle ${settings.otp_enabled ? "on" : ""}`} onClick={() => act(async () => setSettings(await api("/admin/settings", { method: "PATCH", body: JSON.stringify({ otp_enabled: !settings.otp_enabled }) })), "Pengaturan OTP disimpan")}><i /></button>
              </div>
              <div className="setting-row">
                <div><b>Paket default pendaftar baru</b><small>Paket yang otomatis diberikan saat perusahaan baru mendaftar.</small></div>
                <select data-testid="setting-default-plan" className="admin-plan-select" value={settings.default_plan} onChange={(e) => act(async () => setSettings(await api("/admin/settings", { method: "PATCH", body: JSON.stringify({ default_plan: e.target.value }) })), "Paket default disimpan")}>
                  {packages.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <BankSettings key={JSON.stringify(settings.bank_info)} initial={settings.bank_info} notify={notify} onSaved={setSettings} />
            <LandingSettings key={`ld-${JSON.stringify(settings.landing)}`} initial={settings.landing} notify={notify} onSaved={setSettings} />
            <SmtpSettings key={`smtp-${JSON.stringify(settings.smtp)}`} initial={settings.smtp} notify={notify} onSaved={setSettings} />
            <TemplatesEditor key={`tpl-${JSON.stringify(settings.template_meta)}`} templates={settings.email_templates} meta={settings.template_meta} notify={notify} onSaved={setSettings} />
          </>
        )}
        {pkgModal && <PackageModal initial={pkgModal.id ? pkgModal : null} notify={notify} onClose={() => setPkgModal(null)} onSaved={(r) => { setPkgModal(null); notify(`Paket ${r.name} disimpan`); load(); }} />}
        {txModal && <TransactionModal tenants={tenants} packages={packages} notify={notify} onClose={() => setTxModal(false)} onSaved={(r) => { setTxModal(false); notify(`Transaksi ${r.number} tercatat — ${r.tenant_name} aktif s/d ${fmtDate(r.expires_at)}`); load(); }} />}
        {resetPass && <ResetPassModal target={resetPass} onClose={() => setResetPass(null)} onTemp={setTempPass} notify={notify} />}
        {proofView && (
          <div className="modal-overlay" data-testid="proof-modal" onClick={() => setProofView(null)}>
            <div className="modal-panel settings-modal proof-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sm-head">
                <div><p className="eyebrow admin">BUKTI TRANSFER</p><h2>{proofView.number}</h2>
                  <p className="muted sm-sub">{proofView.tenant_name} · {money(proofView.amount)}{proofView.unique_code ? ` (kode unik ${proofView.unique_code})` : ""}{proofView.proof_name ? ` · ${proofView.proof_name}` : ""}</p></div>
                <button className="icon-btn" onClick={() => setProofView(null)}><X size={18} /></button>
              </div>
              <div className="sm-body proof-body">
                {proofView.proof_mime === "application/pdf"
                  ? <iframe title="Bukti transfer" src={`${API}/admin/transactions/${proofView.id}/proof`} className="proof-frame" />
                  : <img data-testid="proof-image" src={`${API}/admin/transactions/${proofView.id}/proof`} alt="Bukti transfer" className="proof-img" />}
              </div>
              <div className="sm-foot">
                <a className="secondary-btn" href={`${API}/admin/transactions/${proofView.id}/proof`} target="_blank" rel="noreferrer">Buka di tab baru</a>
                <button className="primary-btn" onClick={() => setProofView(null)}>Tutup</button>
              </div>
            </div>
          </div>
        )}
        {confirmDel && (
          <div className="modal-overlay" data-testid="admin-delete-modal" onClick={() => setConfirmDel(null)}>
            <div className="modal-panel settings-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sm-head"><div><p className="eyebrow admin">KONFIRMASI HAPUS</p>
                <h2>{confirmDel.kind === "tenant" ? `Hapus ${confirmDel.item.name}?` : confirmDel.kind === "package" ? `Hapus paket ${confirmDel.item.name}?` : `Hapus transaksi ${confirmDel.item.number}?`}</h2></div></div>
              <div className="sm-body">
                <p className="muted">
                  {confirmDel.kind === "tenant" && `Seluruh data perusahaan ini (${confirmDel.item.users} pengguna, ${confirmDel.item.invoices} invoice, ${confirmDel.item.journals} jurnal) akan dihapus permanen dan tidak bisa dikembalikan.`}
                  {confirmDel.kind === "package" && "Paket akan dihapus dari master data. Penghapusan ditolak jika masih dipakai tenant."}
                  {confirmDel.kind === "tx" && `Riwayat pembayaran ${money(confirmDel.item.amount)} milik ${confirmDel.item.tenant_name} akan dihapus (paket tenant tidak berubah).`}
                </p>
              </div>
              <div className="sm-foot">
                <button className="secondary-btn" onClick={() => setConfirmDel(null)}>Batal</button>
                <button data-testid="admin-delete-confirm" className="danger-btn" onClick={() => {
                  const { kind, item } = confirmDel; setConfirmDel(null);
                  if (kind === "tenant") act(() => api(`/admin/tenants/${item.id}`, { method: "DELETE" }), `${item.name} dihapus permanen`);
                  else if (kind === "package") act(() => api(`/admin/packages/${item.id}`, { method: "DELETE" }), `Paket ${item.name} dihapus`);
                  else act(() => api(`/admin/transactions/${item.id}`, { method: "DELETE" }), "Transaksi dihapus");
                }}>Ya, hapus</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
